import { Platform, View } from "react-native"
import Tooltip from "rn-tooltip"

import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import BFText, { Body, H4 } from "@/components/standard/BFText"
import { BFColors } from "@/constants/Colors"
import InfoIcon from "@/assets/icons/tooltip-i-notfilled.svg"
import Tooltips from "@/constants/ToolTips"
import { useQuery } from "@tanstack/react-query"
import { loanDashboardQueryOptions } from "@/queries/loan-dashboard"
import { displayCurrency } from "@/utils/NumberUtils"

export function LoanTotalsSection({ loanId }: { loanId: string }) {
  const loanApplicationQuery = useQuery({
    ...loanDashboardQueryOptions(loanId),
    select: (data) => {
      if (!data) {
        return null
      }

      return {
        totalLoanAmount: data.totalLoanAmount,
        monthlyPayment: data.monthlyPayment,
        financingFee: data.financingFee,
        estimatedCashToClose: data.estimatedCashToClose,
      }
    },
  })

  const loanData = loanApplicationQuery.data

  if (!loanData) {
    return null
  }

  return (
    <Vertical className="justify-start gap-2">
      <Vertical className="justify-start gap-[2px]">
        <H4>Loan Totals</H4>
        <Body>
          For more details, please refer to your provided term sheet, or contact
          your Backflip loan expert.
        </Body>
      </Vertical>
      <Vertical className="bg-white border border-light-grey rounded-md items-center self-stretch py-2.5 px-5">
        <LoanTotalsItem
          label="Total Loan"
          value={displayCurrency({ value: loanData.totalLoanAmount })}
          tooltip={Tooltips.totalLoanAmount}
        />
        <LoanTotalsItem
          label="Monthly Payment"
          value={displayCurrency({ value: loanData.monthlyPayment })}
          tooltip={Tooltips.monthlyPayment}
        />
        <LoanTotalsItem
          label="Financing Fees"
          value={displayCurrency({ value: loanData.financingFee })}
          tooltip={Tooltips.oneTimeFinancing}
        />
        <LoanTotalsItem
          label="Cash to Close"
          value={displayCurrency({ value: loanData.estimatedCashToClose })}
          tooltip={Tooltips.cashToClose}
        />
      </Vertical>
    </Vertical>
  )
}

function LoanTotalsItem({
  label,
  value,
  tooltip,
}: {
  label: string
  value: string
  tooltip?: string
}) {
  return (
    <Horizontal className="items-center gap-2.5 self-stretch">
      <Horizontal className="p-2.5 pl-0 gap-[2px] items-center justify-start">
        <Body>{label}</Body>
        <ItemToolTip tooltip={tooltip} />
      </Horizontal>
      <Horizontal className="items-center justify-end flex-1 p-2.5 pr-0">
        <H4>{value}</H4>
      </Horizontal>
    </Horizontal>
  )
}

function ItemToolTip({ tooltip }: { tooltip?: string }) {
  if (!tooltip) {
    return null
  }

  return (
    <Tooltip
      popover={
        <BFText className="font-spacegrotesk text-white text-[12px] p-[2px]">
          {tooltip}
        </BFText>
      }
      actionType="press"
      backgroundColor={BFColors.calmBlue}
      withOverlay={false}
      height={"auto"}
      width={250}
      containerStyle={{
        borderRadius: 2,
        marginBottom: Platform.OS === "android" ? -21 : 0, // Fixes odd arrow placement that occurs only on Android
      }}
      toggleWrapperProps={{
        hitSlop: 12,
      }}
    >
      <View>
        <InfoIcon width={12} height={12} />
      </View>
    </Tooltip>
  )
}
